import { useRef } from 'react';
import styled from 'styled-components';
import { Card, CardHeader, StepNumber, Textarea, SecondaryButton, Hint, Notice, color } from '../App.styles';
import { formatDuration } from '../minutes';

const TABS = [
  { key: 'text', label: '텍스트 붙여넣기' },
  { key: 'file', label: '파일 업로드' },
  { key: 'audio', label: '음성 업로드' },
];

// 아이폰 음성 메모(.m4a), 안드로이드 녹음 앱(.m4a .3gp .3ga .amr .aac .ogg .opus) 및 일반 음성 파일
// audio/* 를 함께 넣어야 휴대폰에서 녹음 파일 선택 화면이 열린다
const AUDIO_ACCEPT =
  'audio/*,.m4a,.mp4,.3gp,.3ga,.amr,.aac,.mp3,.wav,.ogg,.oga,.opus,.webm,.caf,.flac,.wma';

function InputCard({
  mode,
  onModeChange,
  transcript,
  onTranscriptChange,
  file,
  onFileChange,
  audioFile,
  onAudioFileChange,
  audioDuration,
  quota,
  quotaExhausted,
  audioTooLong,
  maxChars,
}) {
  const usingGroq = quota && quota.engine === 'groq';
  const fileRef = useRef(null);
  const audioRef = useRef(null);

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    e.target.value = '';
    if (selected) onFileChange(selected);
  };

  const handleAudioChange = (e) => {
    const selected = e.target.files[0];
    e.target.value = '';
    if (selected) onAudioFileChange(selected);
  };

  return (
    <Card>
      <CardHeader>
        <StepNumber>3</StepNumber>
        <h2>회의 기록 입력</h2>
      </CardHeader>

      <Tabs>
        {TABS.map(({ key, label }) => (
          <Tab key={key} $active={mode === key} onClick={() => onModeChange(key)}>
            {label}
          </Tab>
        ))}
      </Tabs>

      {mode === 'text' && (
        <>
          <Textarea
            value={transcript}
            placeholder="회의 녹취록이나 메모를 붙여넣어 주세요."
            onChange={(e) => onTranscriptChange(e.target.value)}
          />
          <Counter $over={transcript.length > maxChars}>
            {transcript.length.toLocaleString()}자
            {transcript.length > maxChars && ` / 최대 ${maxChars.toLocaleString()}자`}
          </Counter>
        </>
      )}

      {mode === 'file' && (
        <DropBox>
          <SecondaryButton onClick={() => fileRef.current.click()}>파일첨부</SecondaryButton>
          {file && (
            <FileName>
              {file.name}
              <button onClick={() => onFileChange(null)} aria-label="첨부 파일 삭제">
                ✕
              </button>
            </FileName>
          )}
          <Hint>업로드 가능 파일 : .txt, .docx</Hint>
          <input ref={fileRef} type="file" accept=".txt,.docx" hidden onChange={handleFileChange} />
        </DropBox>
      )}

      {mode === 'audio' && (
        <DropBox>
          <SecondaryButton onClick={() => audioRef.current.click()} disabled={quotaExhausted}>
            녹음 파일 선택
          </SecondaryButton>
          {audioFile && (
            <FileName>
              {audioFile.name} ({(audioFile.size / 1024 / 1024).toFixed(1)}MB
              {audioDuration !== null && ` · ${formatDuration(audioDuration)}`})
              <button onClick={() => onAudioFileChange(null)} aria-label="녹음 파일 삭제">
                ✕
              </button>
            </FileName>
          )}
          <Hint>
            아이폰 음성 메모, 안드로이드 녹음 파일을 올릴 수 있습니다.
            <br />
            (.m4a, .3gp, .amr, .aac, .mp3, .wav, .ogg 등 · 최대 3시간)
          </Hint>
          {usingGroq && !quotaExhausted && (
            <Quota>오늘 남은 무료 변환 시간: {formatDuration(quota.remainingSeconds)}</Quota>
          )}
          <input ref={audioRef} type="file" accept={AUDIO_ACCEPT} hidden onChange={handleAudioChange} />
        </DropBox>
      )}
      {mode === 'audio' && quotaExhausted && (
        <ErrorNotice>
          오늘 무료 변환 시간을 모두 사용했습니다
          {quota.blockedUntil ? ` (${formatDuration((quota.blockedUntil - Date.now()) / 1000)} 후 다시 가능)` : ''}. 내일
          다시 시도하거나, 텍스트·파일로 올려 주세요.
        </ErrorNotice>
      )}
      {mode === 'audio' && audioTooLong && (
        <ErrorNotice>
          녹음({formatDuration(audioDuration)})이 오늘 남은 무료 변환 시간({formatDuration(quota.remainingSeconds)})보다
          깁니다. 내일 다시 시도하거나, 텍스트·파일로 올려 주세요.
        </ErrorNotice>
      )}
      {mode === 'audio' && (
        <Notice>
          {usingGroq
            ? '음성은 외부 음성 인식 서비스(Groq)로 전송되어 글로 바뀝니다. 1시간 녹음은 보통 1분 안팎이면 변환됩니다.'
            : '음성은 서버에서 직접 글로 바꿉니다. 녹음 길이에 따라 몇 분 이상 걸릴 수 있습니다.'}
        </Notice>
      )}
    </Card>
  );
}

const Tabs = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 12px;
  padding: 4px;
  background-color: ${color.bg};
  border-radius: 10px;
`;

const Tab = styled.button`
  flex: 1;
  height: 36px;
  background-color: ${(props) => (props.$active ? '#fff' : 'transparent')};
  border-radius: 8px;
  box-shadow: ${(props) => (props.$active ? '0 1px 3px rgba(0, 0, 0, 0.12)' : 'none')};
  font-size: 14px;
  font-weight: ${(props) => (props.$active ? 700 : 400)};
  color: ${(props) => (props.$active ? color.text : color.textMuted)};
`;

const Quota = styled.p`
  margin-top: 8px;
  font-size: 13px;
  font-weight: 500;
  color: ${color.primary};
`;

const ErrorNotice = styled.div`
  margin-top: 12px;
  padding: 10px 12px;
  background-color: #fdecea;
  border-radius: 8px;
  font-size: 13px;
  line-height: 1.6;
  color: #b3261e;
`;

const Counter = styled.div`
  margin-top: 6px;
  font-size: 13px;
  text-align: right;
  color: ${(props) => (props.$over ? '#C0392B' : color.textMuted)};
`;

const DropBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 32px 16px;
  background-color: ${color.bgSoft};
  border: 1px dashed ${color.border};
  border-radius: 10px;
`;

const FileName = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  font-size: 14px;

  button {
    font-size: 12px;
    color: ${color.textMuted};
  }
`;

export default InputCard;
